import "./styles.scss";

import { Trans, useTranslation } from "react-i18next";

import { Text, Button, Input, Icon, InfoBlock } from "@fragments";
import { CommunityBanner } from "@components/Community";
import Parser from "@helpers/parser";
import {
  IconBuildingCommunity,
  IconUsers,
  IconPhoto,
  IconArrowRight,
  IconPlus,
  IconCircleCheckFilled,
  IconPercentage,
  IconClockHour4,
  IconCoin,
  IconFileText,
  IconSchool,
  IconShieldCheck,
  IconArrowLeft,
} from "@tabler/icons-react";

// Duration unit labels resolve at render; the unit id is the API value.
const durationUnitLabel = (t, unit) =>
  ({
    days: t("durationUnits.days"),
    weeks: t("durationUnits.weeks"),
    months: t("durationUnits.months"),
  })[unit] ?? "";

/**
 * Creator — attach community (§6.10). Two-column task page: the application
 * (choose a community → accept the contract) on the left with a sticky action
 * bar pinned to the bottom of that column, and a sticky program details card on
 * the right. Submit is gated until both a community is picked and the contract
 * is accepted; on success the controller routes to the detail.
 */
export default function AttachCommunityView({
  enrollment,
  loading,
  notFound,
  isAwaitingCommunity,
  ownedCommunities,
  selectedId,
  setSelectedId,
  contractAccepted,
  setContractAccepted,
  submitting,
  canSubmit,
  submit,
  openCreate,
  onOpenDetail,
}) {
  const { t } = useTranslation("member");

  if (loading) {
    return (
      <div className="attach-mentoring px-4 pt-5">
        <div className="am-head-skel skel" />
        <div className="am-grid">
          <div className="am-main">
            <div className="am-skel-card skel" style={{ height: 220 }} />
            <div className="am-skel-card skel" style={{ height: 180 }} />
          </div>
          <div className="am-skel-card skel" style={{ height: 320 }} />
        </div>
      </div>
    );
  }

  if (notFound || !enrollment) {
    return (
      <div className="attach-mentoring px-4 pt-5">
        <div className="am-empty">
          <Icon.Sphere size={72}>
            <IconSchool />
          </Icon.Sphere>
          <Text.h4 contrast={6}>{t("attach.notFound.title")}</Text.h4>
          <Text.span contrast={5}>
            {t("attach.notFound.description")}
          </Text.span>
        </div>
      </div>
    );
  }

  // Guard — only an awaiting_community enrollment can be attached. For any other
  // status, surface a friendly block with a path back to the mentoring detail.
  if (!isAwaitingCommunity) {
    return (
      <div className="attach-mentoring px-4 pt-5">
        <div className="am-empty">
          <Icon.Sphere size={72}>
            <IconBuildingCommunity />
          </Icon.Sphere>
          <Text.h4 contrast={6}>{t("attach.notAwaiting.title")}</Text.h4>
          <Text.span contrast={5}>
            {t("attach.notAwaiting.description")}
          </Text.span>
          <Button
            text={t("attach.notAwaiting.button")}
            color="inverse"
            endIcon={<IconArrowRight />}
            onClick={onOpenDetail}
            preventLoading
          />
        </div>
      </div>
    );
  }

  const { program } = enrollment;
  const splitPct =
    enrollment.effective_split_percentage ?? program?.split_percentage;
  const hasOwned = ownedCommunities.length > 0;
  const selectedCommunity = ownedCommunities.find((c) => c.id === selectedId);

  return (
    <div className="attach-mentoring px-4 pt-5">
      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <header className="am-head">
        <button className="am-back" onClick={onOpenDetail} type="button">
          <IconArrowLeft size={16} />
          <Text.span contrast={5}>{t("attach.back")}</Text.span>
        </button>
        <Text.h1 weight={500} left={<IconBuildingCommunity />}>
          {t("attach.title")}
        </Text.h1>
        <Text.span contrast={6} className="am-subtitle">
          <Trans
            t={t}
            i18nKey="attach.subtitle"
            values={{ program: program?.name }}
            components={{ strong: <strong /> }}
          />
        </Text.span>
      </header>

      <div className="am-grid">
        {/* ── Left: application (steps) + sticky action bar ─────────────────── */}
        <div className="am-main">
          {/* Step 1 — choose a community */}
          <section className="am-card" style={{ "--i": 0 }}>
            <div className="am-card-head">
              <span className="am-step">1</span>
              <div className="am-card-head-text">
                <Text.h5 contrast={8} weight={500}>
                  {t("attach.step1.title")}
                </Text.h5>
                <Text.span contrast={5}>
                  {t("attach.step1.description")}
                </Text.span>
              </div>
            </div>

            {hasOwned ? (
              <div className="am-communities">
                {ownedCommunities.map((community, i) => (
                  <CommunityOption
                    key={community.id}
                    community={community}
                    index={i}
                    selected={selectedId === community.id}
                    disabled={submitting}
                    onSelect={() => setSelectedId(community.id)}
                  />
                ))}

                <button
                  className="am-create"
                  type="button"
                  onClick={openCreate}
                  disabled={submitting}
                >
                  <span className="am-create-icon">
                    <IconPlus size={18} />
                  </span>
                  <Text.span contrast={7} weight={500}>
                    {t("attach.createCommunity")}
                  </Text.span>
                </button>
              </div>
            ) : (
              <div className="am-no-communities">
                <InfoBlock
                  title={t("attach.noCommunities.title")}
                  description={t("attach.noCommunities.description")}
                />
                <Button
                  text={t("attach.createCommunity")}
                  color="primary"
                  startIcon={<IconPlus />}
                  onClick={openCreate}
                  preventLoading
                />
              </div>
            )}
          </section>

          {/* Step 2 — accept the contract */}
          <section className="am-card" style={{ "--i": 1 }}>
            <div className="am-card-head">
              <span className="am-step">2</span>
              <div className="am-card-head-text">
                <Text.h5 contrast={8} weight={500}>
                  {t("attach.step2.title")}
                </Text.h5>
                <Text.span contrast={5}>
                  {program?.contract_url
                    ? t("attach.step2.descriptionWithContract")
                    : t("attach.step2.descriptionWithoutContract")}
                </Text.span>
              </div>
            </div>

            {program?.contract_url && (
              <a
                className="am-contract-link"
                href={program.contract_url}
                target="_blank"
                rel="noreferrer"
              >
                <Text.span contrast={7} left={<IconFileText size={15} />}>
                  {t("attach.step2.readContract")}
                </Text.span>
              </a>
            )}

            {/* Single clickable control — the label is the switch's own slot, so
                clicking the text toggles once (no native-label double-toggle). */}
            <div className="am-accept">
              <Input.Switch
                size="small"
                value={contractAccepted}
                onInput={setContractAccepted}
                disabled={submitting}
                label={t("attach.step2.accept")}
              />
            </div>
          </section>

          {/* Sticky action bar — pinned to the bottom of the left column. */}
          <div className="am-actionbar">
            <div className="am-actionbar__card">
              <div className="am-actionbar__info">
                <span className="am-actionbar__icon">
                  <IconBuildingCommunity size={18} />
                </span>
                <div className="am-actionbar__text">
                  <Text.span contrast={9} weight={500}>
                    {t("attach.actionBar.title")}
                  </Text.span>
                  <Text.span contrast={5} maxLines={1}>
                    {selectedCommunity
                      ? selectedCommunity.name
                      : t("attach.actionBar.selectHint")}
                  </Text.span>
                </div>
              </div>

              <div className="am-actionbar__actions">
                <Button
                  text={t("common:actions.cancel")}
                  size="small"
                  color="overlay"
                  variant="rounded"
                  onClick={onOpenDetail}
                  preventLoading
                />
                <Button
                  text={t("attach.actionBar.submit")}
                  size="small"
                  color="primary"
                  endIcon={<IconArrowRight size={16} />}
                  disabled={!canSubmit}
                  onClick={submit}
                />
              </div>
            </div>
          </div>
        </div>

        {/* ── Right: program details card (sticky) ──────────────────────────── */}
        <aside className="am-aside">
          <section className="am-summary">
            <div className="am-summary-media">
              {program?.banner?.url ? (
                <img
                  src={program.banner.url}
                  alt={program.name}
                  loading="lazy"
                />
              ) : (
                <div className="am-summary-media-fallback">
                  <IconPhoto size={28} />
                </div>
              )}
            </div>
            <div className="am-summary-body">
              <Text.h4 contrast={9} weight={500} maxLines={2}>
                {program?.name}
              </Text.h4>

              <div className="am-summary-rows">
                <SummaryRow
                  icon={<IconPercentage size={14} />}
                  label={t("attach.summary.splitPercentage")}
                  value={splitPct != null ? `${splitPct}%` : "—"}
                  emphasis
                />
                <SummaryRow
                  icon={<IconClockHour4 size={14} />}
                  label={t("attach.summary.duration")}
                  value={
                    program?.duration_value
                      ? `${program.duration_value} ${durationUnitLabel(t, program.duration_unit)}`.trim()
                      : t("attach.summary.durationUndefined")
                  }
                />
                <SummaryRow
                  icon={<IconCoin size={14} />}
                  label={t("attach.summary.splitWindow")}
                  value={
                    program?.split_duration_value
                      ? `${program.split_duration_value} ${durationUnitLabel(t, program.split_duration_unit)}`.trim()
                      : t("attach.summary.splitWindowSameAsDuration")
                  }
                />
              </div>

              <div className="am-summary-note">
                <Text.span contrast={6} left={<IconShieldCheck />}>
                  {t("attach.summary.note")}
                </Text.span>
              </div>
            </div>
          </section>
        </aside>
      </div>
    </div>
  );
}

// A selectable community row — banner, name, member count + a check indicator.
// Selected uses a high-contrast (inverted) chip rather than a primary tint.
function CommunityOption({ community, index, selected, disabled, onSelect }) {
  const { t } = useTranslation("member");
  const total = community?.members?.total ?? 0;

  return (
    <button
      type="button"
      className={`am-community${selected ? " is-selected" : ""}`}
      style={{ "--i": index }}
      onClick={onSelect}
      disabled={disabled}
      aria-pressed={selected}
    >
      <CommunityBanner banner={community.banner} size={44} />
      <div className="am-community-info">
        <Text.h5 contrast={selected ? 2 : 8} weight={500} maxLines={1}>
          {community.name}
        </Text.h5>
        <Text.span contrast={selected ? 4 : 5} left={<IconUsers size={13} />}>
          {t("attach.members", { total: Parser.number(total) })}
        </Text.span>
      </div>
      <span className="am-radio" aria-hidden="true">
        {selected ? (
          <IconCircleCheckFilled size={22} className="csf-8" />
        ) : (
          <span className="am-radio-dot" />
        )}
      </span>
    </button>
  );
}

function SummaryRow({ icon, label, value, emphasis }) {
  return (
    <div className="am-summary-row">
      <Text.span contrast={5} left={icon}>
        {label}
      </Text.span>
      <Text.span contrast={emphasis ? 9 : 8} weight={emphasis ? 500 : 300}>
        {value}
      </Text.span>
    </div>
  );
}
